import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, ChevronLeft, Clock3, Flame, Monitor, RotateCcw, Scissors, Share2, Sparkles, Target, Trophy, Volume2, VolumeX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import DcAppShell from './deadcenter/DcAppShell';
import { Toaster, toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './components/ui/dialog';
import { SliceSurface } from './game/SliceSurface';
import { ResultReveal } from './game/ResultReveal';
import { generateShape, multiPath, splitPolygon } from './game/geometry.mjs';
import { createStoredChallenge } from './game/shapes.mjs';
import { finalizeRecords, formatPercent, readRecords, saveRecords, scoreCut } from './game/rules.mjs';
import { haptic, playSound, unlockSound } from './game/sound';
import { PlatformProvider, usePlatform, DailyHomeEntry } from './platform/PlatformContext';
import { PlatformScreens } from './platform/PlatformScreens';
import PublicSite, { SiteFooter, SiteHeader } from './site/Site';
import { PerfectSliceIcon, PerfectSliceWordmark } from './brand/PerfectSliceBrand';
import { ShareResultDialog } from './brand/ShareResultDialog';
import { EndlessSaveStatus } from './platform/EndlessHistory';
import { EndlessAccountRecord } from './platform/EndlessRecord';
import { DailyReminder } from './platform/DailyReminder';
import { CookieBanner } from './components/CookieBanner';
import { trackOnce, trackEvent } from './lib/analytics';
import './App.css';
import './brand/identity.css';
import './platform/endless-history.css';

const TOAST_OPTIONS = { className: 'slice-toast' };
const number = value => value.toLocaleString('en-US');
const bestOf = attempts => attempts.reduce((best, attempt) => !best || attempt.difference < best.difference ? attempt : best, null);

export const EndlessResultPanel = ({ game, complete, featured, result, best, bestIndex, recordBadge, nextShape, tryAgain, share }) => <section className={`result-panel tier-${featured.tier}`} data-testid="result-panel">
  <div data-testid={complete ? 'round-summary-best' : 'attempt-result'}>
    {complete ? <div className="attempt-history" aria-label="All three attempts">{game.attempts.map((attempt,i) => <div key={`${game.roundId}:attempt-${i + 1}`} className={`attempt-history-item ${i === bestIndex ? 'is-best' : ''}`} data-testid={`round-summary-attempt-${i + 1}`}>
      <span className="history-attempt">{i === bestIndex ? <><Trophy size={12}/> BEST · {i + 1}</> : `ATTEMPT ${i + 1}`}</span><strong>{formatPercent(attempt.percent)}<small>%</small></strong><span>{formatPercent(attempt.difference)}% off</span><b>{attempt.points} pts</b>
    </div>)}</div> : <div className="best-attempt" data-testid="result-best-attempt">{result.improvement !== null && result.improvement > 0 ? <><ArrowUpRight size={15}/>{formatPercent(result.improvement)}% closer. </> : null}<span>Next up: <b>beat {formatPercent(best.difference)}% off</b></span></div>}
    <div className="session-award">{sessionAward(complete, result.awarded)}</div>
    {recordBadge && <div className="record-badge" data-testid="new-best-badge"><Sparkles size={13}/><b>NEW BEST!</b> {recordBadge}</div>}
  </div>
  <div className="result-actions"><button ref={button => { if (button && document.activeElement?.getAttribute('data-testid') === 'game-surface') button.focus({ preventScroll: true }); }} className="primary-button" data-testid={complete ? 'result-next-shape-button' : 'result-try-again-button'} onClick={complete ? nextShape : tryAgain}>{complete ? 'NEXT SHAPE' : 'TRY AGAIN'}{complete ? <ArrowRight size={19}/> : <RotateCcw size={17}/>}</button><button data-testid="result-share-button" className="share-button" onClick={share} aria-label="Share your best slice" title="Share your best slice"><Share2 size={19}/></button></div>
  {!complete && <button className="text-button next-early" data-testid="result-next-shape-button" onClick={nextShape}>Next shape<ArrowRight size={14}/></button>}
  <EndlessSaveStatus roundId={game.roundId} completed={complete}/>
</section>;

function sessionAward(complete, awarded) {
  if (complete) return 'Your best cut counts toward your session';
  if (awarded > 0) return `+${number(awarded)} to your session`;
  return 'Your best score stays safe';
}

function HeroArt() {
  const split = useMemo(() => splitPolygon(generateShape(5349, 2), [20, 224], [340, 111]), []);
  return <div className="hero-art" aria-label="An orange organic shape separated by one clean diagonal slice" role="img">
    <div className="art-caption"><span className="tiny-dot"/> A LITTLE CUT. A BIG FEELING.</div>
    <svg viewBox="0 0 420 370" className="hero-blob" aria-hidden="true">
      <defs>
        <linearGradient id="hero-orange" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#ffb461"/><stop offset="1" stopColor="#ff8852"/></linearGradient>
        <filter id="hero-shadow" x="-40%" y="-40%" width="180%" height="200%"><feDropShadow dx="0" dy="14" stdDeviation="12" floodColor="#d47d48" floodOpacity=".15"/></filter>
      </defs>
      <ellipse cx="220" cy="326" rx="111" ry="12" fill="#ede8de" opacity=".5"/>
      <g transform="translate(30,19)" filter="url(#hero-shadow)">
        <path d={multiPath(split.remainder)} transform="translate(2,8)" fill="url(#hero-orange)" stroke="#da8245" strokeWidth=".7"/>
        <g className="hero-top-piece"><path d={multiPath(split.selected)} fill="url(#hero-orange)" stroke="#da8245" strokeWidth=".7"/><path d={multiPath(split.selected)} fill="white" fillOpacity=".08"/></g>
      </g>
      <path className="hero-dash" d="M22 263L392 130" fill="none" stroke="#273d39" strokeWidth="1.5" strokeDasharray="5 6"/>
      <circle cx="22" cy="263" r="5" fill="#faf9f6" stroke="#273d39" strokeWidth="1.5"/>
      <path d="M382 126l10 4-5 10" fill="none" stroke="#273d39" strokeWidth="1.5"/>
      <path d="M43 104h12m-6-6v12M344 310h10m-5-5v10" stroke="#b3b9b0" strokeWidth="1.5"/>
    </svg>
    <div className="target-sticker"><span>THE TARGET</span><strong>37<small>%</small></strong><Sparkles size={16}/></div>
    <div className="art-note"><svg viewBox="0 0 75 37" aria-hidden="true"><path d="M70 30Q16 41 14 5m-7 8 7-8 8 7"/></svg><span>trust your gut.</span></div>
    <span className="art-dot dot-one"/><span className="art-dot dot-two"/>
  </div>;
}
/* ─────────────────────────────────────────────────────────────────────────
   Perfect Slice — "How to Play" feature section
   Mirrors the Dead Center DeadCenterHome dch-feature layout exactly:
     Left  → badge + heading + copy + metadata + CTA button
     Right → 3 step-rule cards on an orange-tinted background
───────────────────────────────────────────────────────────────────────── */
const PsHowToPlay = ({ onPlay }) => (
  <section className="ps-feature" aria-labelledby="ps-feature-title">
    <div className="ps-section-label">
      <span className="ps-eyebrow" data-testid="ps-how-to-play-eyebrow">HOW TO PLAY</span>
      <span className="ps-rule" aria-hidden="true"/>
      <span className="ps-index" aria-hidden="true">01 / PERFECT SLICE</span>
    </div>
    <div className="ps-feature-card" data-testid="ps-feature-card">

      {/* Left column — copy + CTA */}
      <div className="ps-feature-copy">
        <Badge variant="outline" className="ps-feature-badge" data-testid="ps-feature-badge">
          <Scissors size={12} aria-hidden="true"/> PERFECT SLICE
        </Badge>
        <h2 id="ps-feature-title" data-testid="ps-feature-heading">
          Slice it to the<br/>exact percentage.
        </h2>
        <p data-testid="ps-feature-description">
          Each shape comes with a target percentage. One straight cut to match it as closely as you can.
          <br/><strong>Three tries — your best counts.</strong>
        </p>
        <div className="ps-meta" data-testid="ps-feature-meta">
          <span><Clock3 size={13} aria-hidden="true"/> 30 sec</span>
          <span><Scissors size={13} aria-hidden="true"/> Precision</span>
          <span><Monitor size={13} aria-hidden="true"/> Play in browser</span>
        </div>
        <button
          className="ps-play-btn"
          data-testid="ps-feature-play-button"
          onClick={onPlay}
        >
          PLAY TODAY'S PERFECT SLICE
          <ArrowRight size={18} aria-hidden="true"/>
        </button>
      </div>

      {/* Right column — 3 game rules */}
      <div className="ps-steps-panel" data-testid="ps-steps-panel">
        {[
          {
            icon: '3',
            title: 'Three attempts per day',
            desc: 'Your best cut counts. Each try gets you a little closer.',
          },
          {
            icon: '—',
            title: 'One straight cut',
            desc: 'Draw a single straight line to slice the shape. No take-backs.',
          },
          {
            icon: '%',
            title: 'Match the target',
            desc: 'The closer your cut to the target percentage, the higher your score.',
          },
        ].map(({ icon, title, desc }, i) => (
          <div key={i} className="ps-step" data-testid={`ps-step-${i + 1}`}>
            <span className="ps-step-icon" aria-hidden="true">{icon}</span>
            <div>
              <strong className="ps-step-title">{title}</strong>
              <p className="ps-step-desc">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ── Hero lockup for the Perfect Slice home screen (mirrors Dead Center's approach) ── */
const PsHeroLockup = ({ onParent }) => (
  <div className="ps-identity ps-hero-lockup" data-testid="ps-hero-lockup">
    <PerfectSliceIcon/>
    <PerfectSliceWordmark testId="ps-hero-wordmark"/>
    <a
      href="/"
      className="ps-endorsement"
      data-testid="ps-hero-cubitplay-link"
      onClick={event => {
        if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          onParent();
        }
      }}
    >
      <span>by <b>CubitPlay</b></span>
    </a>
  </div>
);

function App() {
  return <PlatformProvider><GameApp/><CookieBanner/></PlatformProvider>;
}
function GameApp() {
  const { view, navigate, user, openAuth, authOpen, endless } = usePlatform();
  const [completedShapes, setCompletedShapes] = useState(0);
  const invitationShown = useRef(false);
  const [screen, setScreen] = useState('home');
  const [game, setGame] = useState(null);
  const gameRef = useRef(null);
  const [records, setRecords] = useState(readRecords);
  const recordRef = useRef(records);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [sound, setSound] = useState(() => { try { return localStorage.getItem('perfect-slice.sound') === 'true'; } catch { return false; } });
  const [error, setError] = useState('');
  const [recordBadge, setRecordBadge] = useState('');
  const [shareData, setShareData] = useState(null);
  useEffect(() => { setShareData(null); }, [view]);
  const storageWarning = useRef(false);
  const updateGame = next => { gameRef.current = next; setGame(next); };
  const updateRecords = next => {
    recordRef.current = next; setRecords(next);
    if (!saveRecords(next) && !storageWarning.current) {
      storageWarning.current = true;
      toast('Records are saved for this visit only', { description: 'Your browser is not allowing local storage.' });
    }
  };
  const toggleSound = () => {
    const enabled = !sound;
    setSound(enabled);
    try { localStorage.setItem('perfect-slice.sound', String(enabled)); } catch { /* Optional preference. */ }
    if (enabled) { unlockSound(); playSound('good'); }
  };
  const play = () => {
    if (view !== '/perfectslice') navigate('/perfectslice');
    if (!gameRef.current) {
      let seed;
      try { seed = crypto.getRandomValues(new Uint32Array(1))[0]; } catch { seed = Date.now() >>> 0; }
      // Capture IDs before updateGame so the exact same values reach both the
      // game state and the dedup keys — prevents any ref-timing ambiguity.
      const newSessionId = crypto.randomUUID();
      const newRoundId = crypto.randomUUID();
      updateGame({ seed, sessionId: newSessionId, roundId: newRoundId, challenge: createStoredChallenge(seed, 1), attempts: [], result: null, score: 0, streak: 0, finalized: false });
      trackOnce('game_start',   newSessionId, { game_mode: 'endless' });
      trackOnce('level_start',  newRoundId,   { game_mode: 'endless', logged_in: !!user?.alias_confirmed });
    }
    if (sound) unlockSound();
    setScreen('game'); setError('');
  };
  const finalize = (current, currentRecords) => {
    if (current.finalized || !current.attempts.length) return { next: current, nextRecords: currentRecords };
    const best = bestOf(current.attempts), streak = best.difference <= 2 + 1e-9 ? current.streak + 1 : 0;
    endless.enqueue({ id: current.roundId, session_id: current.sessionId, session_seed: current.seed, shape_seed: current.challenge.seed, round: current.challenge.round, target: current.challenge.target,
      cuts: current.attempts.map(attempt => ({ start: [attempt.start[0] / 360, attempt.start[1] / 320], end: [attempt.end[0] / 360, attempt.end[1] / 320] })), played_at: new Date().toISOString() });
    // Legacy event kept for backward compat with existing GA4 custom dimensions.
    trackOnce('round_complete', current.roundId, { game_mode: 'endless', round_number: current.challenge.round, attempt_count: current.attempts.length, score: best.points, error_percent: best.difference });
    // Google-recommended events with the full parameter set.
    const levelParams = {
      game_mode: 'endless',
      round_number: current.challenge.round,
      attempt_number: current.attempts.length,
      attempt_count: current.attempts.length,
      score: best.points,
      error_percent: best.difference,
      difference_percent: parseFloat(best.difference.toFixed(2)),
      accuracy: parseFloat(Math.max(0, 100 - best.difference).toFixed(2)),
      logged_in: !!user?.alias_confirmed,
    };
    trackOnce('level_end',  `le:${current.roundId}`, levelParams);
    trackOnce('post_score', `ps:${current.roundId}`, levelParams);
    if (current.attempts.length === 3) setCompletedShapes(value => value + 1);
    return { next: { ...current, streak, finalized: true }, nextRecords: finalizeRecords(currentRecords, best, streak) };
  };
  const onCut = split => {
    const current = gameRef.current;
    if (!current || current.result || current.attempts.length >= 3) return;
    const cut = { ...split, ...scoreCut(current.challenge.target, split.percent) };
    const previousBest = bestOf(current.attempts);
    const awarded = Math.max(0, cut.points - (previousBest?.points || 0));
    cut.awarded = awarded;
    cut.improvement = previousBest ? previousBest.difference - cut.difference : null;
    let next = { ...current, result: cut, attempts: [...current.attempts, cut], score: current.score + awarded };
    const oldRecords = recordRef.current;
    let nextRecords = { ...oldRecords, bestScore: Math.max(oldRecords.bestScore, next.score), bestDifference: Math.min(oldRecords.bestDifference ?? Infinity, cut.difference), perfectSlices: oldRecords.perfectSlices + (cut.tier === 'perfect' ? 1 : 0), legendarySlices: oldRecords.legendarySlices + (cut.tier === 'legendary' ? 1 : 0) };
    let badge = oldRecords.bestDifference !== null && cut.difference < oldRecords.bestDifference ? 'Your most accurate slice' : oldRecords.bestScore > 0 && next.score > oldRecords.bestScore ? 'Highest session score' : '';
    if (next.attempts.length === 3) {
      const finished = finalize(next, nextRecords);
      next = finished.next; nextRecords = finished.nextRecords;
      if (next.streak > oldRecords.longestStreak && oldRecords.longestStreak > 0) badge = 'Your longest Endless streak';
    }
    updateGame(next); updateRecords(nextRecords); setRecordBadge(badge); setError('');
    if (sound) {
      playSound('slice');
      if (cut.difference <= 1 + 1e-9) playSound(cut.tier === 'legendary' ? 'legendary' : cut.difference <= .5 + 1e-9 ? 'celebrate' : 'good');
    }
    haptic(cut.difference <= .5 + 1e-9 ? 'perfect' : cut.difference <= 1 + 1e-9 ? 'excellent' : 'ordinary');
  };
  const tryAgain = () => {
    const current = gameRef.current;
    if (!current?.result || current.attempts.length >= 3) return;
    updateGame({ ...current, result: null }); setError(''); setRecordBadge('');
  };
  const nextShape = () => {
    const current = gameRef.current;
    if (!current?.attempts.length || !current.result) return;
    const { next, nextRecords } = finalize(current, recordRef.current);
    if (next.streak > recordRef.current.longestStreak && recordRef.current.longestStreak > 0) toast('NEW BEST! Your longest Endless streak', { description: `${next.streak} shapes in a row within 2%.` });
    updateRecords(nextRecords);
    // Capture the new round ID so level_start uses the same UUID as the game state.
    const newRoundId = crypto.randomUUID();
    updateGame({ ...next, roundId: newRoundId, challenge: createStoredChallenge(next.seed, next.challenge.round + 1), attempts: [], result: null, finalized: false });
    trackOnce('level_start', newRoundId, { game_mode: 'endless', logged_in: !!user?.alias_confirmed });
    setError(''); setRecordBadge('');
  };
  const share = () => {
    const current = gameRef.current;
    if (!current) return;
    const best = bestOf(current.attempts);
    if (!best) return;
    setShareData({ mode: 'endless', round: current.challenge.round, target: current.challenge.target, best, attempts: current.attempts.length, url: window.location.origin + '/perfectslice' });
  };
  const best = game ? bestOf(game.attempts) : null;
  const complete = game?.attempts.length === 3;
  const result = game?.result;
  const featured = complete ? best : result;
  const bestIndex = game?.attempts.indexOf(best) ?? -1;
  const isGame = screen === 'game' && view === '/perfectslice';
  useEffect(() => {
    if (user?.alias_confirmed) { invitationShown.current = true; return; }
    if (!isGame || !complete || completedShapes < 5 || invitationShown.current || authOpen || recordsOpen || shareData) return;
    const timer = setTimeout(() => { invitationShown.current = true; openAuth('invite'); }, 850);
    return () => clearTimeout(timer);
  }, [user?.alias_confirmed, isGame, complete, completedShapes, authOpen, recordsOpen, shareData, openAuth]);
  const reminder = <DailyReminder blocked={isGame || recordsOpen || !!shareData || authOpen}/>;
  // Dead Center game views get their own dedicated shell (no CubitPlay marketing nav)
  if (view === '/deadcenter/daily' || view === '/deadcenter/leaderboard') return <DcAppShell/>;
  if (!['/perfectslice', '/perfectslice/daily', '/perfectslice/leaderboard'].includes(view)) return <><PublicSite onPlayEndless={play}/>{reminder}<Toaster position="top-center" closeButton toastOptions={TOAST_OPTIONS}/></>;
  return <div className={`app-shell ${isGame ? 'in-game' : 'on-home'} ${view !== '/perfectslice' ? 'platform-route' : ''}`}>
    <SiteHeader/>
    {view !== '/perfectslice' ? <PlatformScreens sound={sound} onPlayEndless={play}/> : !isGame ? <main className="home-main" data-testid="home-screen">
      <section className="home-hero">
        <div className="hero-copy"><PsHeroLockup onParent={() => navigate('/')}/><h1>Think you can<br/><span className="eyeball-word">eyeball it<svg viewBox="0 0 355 20" aria-hidden="true"><path d="M4 13Q159-3 350 8M58 18Q187 7 318 15"/></svg></span>?</h1><p className="hero-description" data-testid="perfectslice-hero-description">One straight cut. Get as close to the target as you can.<br/>Get close. Then get closer.</p><div className="home-play-wrap"><button className="primary-button play-button" data-testid="home-play-button" onClick={() => navigate('/perfectslice/daily')}><Scissors size={21}/>PLAY TODAY'S SLICE<ArrowUpRight size={22}/></button><span className="signup-note"><span className="note-dot"/> No signup. Just slice.</span></div></div>
        <HeroArt/>
      </section>
      <DailyHomeEntry/>
      <PsHowToPlay onPlay={play}/>
    </main> : <main className={`game-main ${result ? 'has-result persistent-result' : ''} ${complete ? 'round-complete' : ''}`} data-testid="game-screen">
      <div className="round-row"><button className="back-button" data-testid="game-back-button" aria-label="Back to home, keep this game" onClick={() => setScreen('home')}><ChevronLeft size={17}/><span>Take a breather</span></button><span data-testid="game-round" className="round-label">ROUND {String(game.challenge.round).padStart(2, '0')}</span><div className="round-hud" aria-label={`Score: ${number(game.score)}, streak: ${game.streak}`}><div className="score-block"><span>SCORE</span><strong data-testid="hud-score">{number(game.score)}</strong></div><div className={`streak-block ${game.streak > 0 ? 'streak-active' : ''}`} title="Endless streak: shapes within 2%" aria-label={`Streak: ${game.streak}`}><Flame size={19}/><strong data-testid="hud-streak">{game.streak}</strong></div></div></div>
      <section className="target-block" aria-label={`Cut off ${game.challenge.target} percent`}><span className="target-label">CUT OFF</span><h1 data-testid="game-target-percent">{game.challenge.target}<span>%</span></h1><span className="target-subline">{result ? 'Take it in. Your next cut can wait.' : best ? `Beat ${formatPercent(best.difference)}% off` : 'Trust your eyes. Make your cut.'}</span></section>
      <SliceSurface challenge={game.challenge} result={featured} onCut={onCut} onInvalid={() => setError('Cut all the way through the shape')}>
        <div className="ps-canvas-controls" data-testid="ps-canvas-controls">
          <button data-testid="hud-records-button" className="ps-canvas-btn" aria-label="Your personal bests" title="Your personal bests" onClick={() => setRecordsOpen(true)}><Trophy size={17}/></button>
          <button data-testid="hud-sound-toggle" className="ps-canvas-btn" aria-label={sound ? 'Turn sound off' : 'Turn sound on'} aria-pressed={sound} title={sound ? 'Sound on' : 'Sound off'} onClick={toggleSound}>{sound ? <Volume2 size={17}/> : <VolumeX size={17}/>}</button>
        </div>
        {result && <ResultReveal result={featured} latestResult={result} complete={complete} bestAttempt={bestIndex + 1} key={`${game.challenge.seed}-${game.attempts.length}`}/>}
      </SliceSurface>
      <div className="attempt-section"><div className="attempt-dots" data-testid="game-attempts" aria-label={`${game.attempts.length} of 3 attempts used`}>{[0, 1, 2].map(i => <span key={i} data-testid={`attempt-dot-${i + 1}`} className={`attempt-dot ${i < game.attempts.length ? 'used' : ''} ${!result && i === game.attempts.length ? 'current' : ''} ${game.attempts[i] === best ? 'best' : ''}`} title={game.attempts[i] ? `Attempt ${i + 1}: ${formatPercent(game.attempts[i].percent)}%, off by ${formatPercent(game.attempts[i].difference)}%` : `Attempt ${i + 1}`}><i/>{game.attempts[i] === best && <span className="best-dot-marker"/>}</span>)}</div><span className="attempt-label" data-testid="game-attempt-label">{result ? complete ? 'THREE CUTS. YOUR BEST COUNTS.' : `ATTEMPT ${game.attempts.length} OF 3` : `ATTEMPT ${game.attempts.length + 1} OF 3`}</span></div>
      {result ? <EndlessResultPanel key={`${game.challenge.seed}-${game.attempts.length}`} game={game} complete={complete} featured={featured} result={result} best={best} bestIndex={bestIndex} recordBadge={recordBadge} nextShape={nextShape} tryAgain={tryAgain} share={share}/> : <div className="ready-area"><div className={`cut-hint ${error ? 'invalid-hint' : ''}`} aria-live="polite" data-testid="game-hint">{error ? <><RotateCcw size={16}/>{error}</> : best ? <><Target size={17}/><span>Beat <strong>{formatPercent(best.difference)}% off</strong></span></> : game.challenge.round === 1 ? <><Scissors size={17}/><span>Drag across the shape to make one straight cut</span></> : <><Scissors size={17}/> One clean cut. Make it count.</>}</div><span className="preview-hint">The highlighted piece is your slice.</span></div>}
      <section className="endless-daily-badge-wrap" data-testid="endless-daily-slice-badge-wrap" aria-label="Daily Slice challenge"><DailyHomeEntry/></section>
    </main>}
    <Dialog open={recordsOpen} onOpenChange={setRecordsOpen}><DialogContent className="records-dialog" data-testid="records-dialog"><div className="records-icon"><Trophy size={25}/></div><DialogTitle className="records-title">Your personal bests<span>.</span></DialogTitle><DialogDescription>{user?.alias_confirmed ? 'Your synced Endless record and device-only personal bests.' : 'Little records. Big satisfaction. Saved on this device.'}</DialogDescription>{recordsOpen && user?.alias_confirmed && <EndlessAccountRecord/>}<h3 className="records-device-heading" data-testid="personal-bests-device-heading">ON THIS DEVICE · ENDLESS</h3><dl className="records-list">{[['Highest session score', number(records.bestScore), 'best-score'], ['Best slice ever', records.bestDifference === null ? 'Your first cut awaits' : `${formatPercent(records.bestDifference)}% off`, 'best-accuracy'], ['Longest Endless streak', `${records.longestStreak} ${records.longestStreak === 1 ? 'shape' : 'shapes'}`, 'longest-streak'], ['Shapes played', number(records.shapesPlayed), 'shapes-played'], ['Perfect slices', number(records.perfectSlices), 'perfect-slices'], ['Legendary slices', number(records.legendarySlices), 'legendary-slices']].map(([label, value, id]) => <div key={id} data-testid={id === 'longest-streak' ? 'personal-bests-longest-endless-streak-row' : `personal-bests-${id}-row`}><dt data-testid={`personal-bests-${id}-label`}>{label}</dt><dd data-testid={`record-${id}`}>{value}</dd></div>)}</dl><p className="records-score-note">Up to 1,000 points per slice. Your best cut counts.</p><p className="records-footnote" data-testid="personal-bests-longest-endless-streak-helper"><Flame size={14}/> A streak continues when your best cut is within 2% of the target.</p><p className="records-history-note" data-testid="personal-bests-history-note">All-time device records include older sessions and days.</p><button className="primary-button" data-testid="records-continue-button" onClick={() => setRecordsOpen(false)}>BACK TO SLICING<ArrowRight size={18}/></button></DialogContent></Dialog>
    {reminder}
    <ShareResultDialog data={shareData} onClose={() => setShareData(null)}/>
    {!isGame && <SiteFooter/>}
    <Toaster position="top-center" richColors closeButton toastOptions={TOAST_OPTIONS}/>
  </div>;
}
export default App;
